import { isChromeBrowser, isIos } from 'common/js/environment-utils';
import { DebugLog } from 'common/js/debug-log';

interface HeavyAdMonitor {
  element: HTMLIFrameElement;
  observer: any;
  listener: (this: Window, ev: Event) => void;
}

export class HeavyAdService {
  private readonly elements: HTMLIFrameElement[] = [];
  private heavyAdMonitors: HeavyAdMonitor[] = [];

  constructor(
    private readonly rootFrame: HTMLIFrameElement,
    private readonly onInterventionCallback: (reports: Report[]) => void
  ) {
    if (!this.shouldHandleHeavyAdInterventions()) return;

    this.elements = this.getIframesToWatchForHeavyIntervention(window);
  }

  listenToHeavyAdIntervention(): void {
    this.elements.forEach(element => {
      try {
        if (element.contentWindow) this.setupObserver(element);
      } catch (e) {
        if (e instanceof DOMException) DebugLog.log(e.message);
      }
    });
  }

  destroy() {
    this.heavyAdMonitors?.forEach(({ element, listener, observer }) => {
      element.contentWindow?.removeEventListener('pagehide', listener);
      observer.disconnect();
    });

    this.heavyAdMonitors = [];
  }

  private setupObserver(element: HTMLIFrameElement) {
    if (!element.contentWindow?.ReportingObserver) return;

    const observer = new element.contentWindow.ReportingObserver(
      (reports: Report[]) => this.onIntervention(reports),
      {
        types: ['intervention'],
        buffered: true
      }
    );

    const listener = () => {
      this.onIntervention(observer.takeRecords());
    };

    this.heavyAdMonitors.push({ element, observer, listener });
    observer.observe();
    element.contentWindow.addEventListener('pagehide', listener);
  }

  private onIntervention(reports: Report[]) {
    const filteredReports = reports.filter(
      (report: Report) =>
        report.body?.id && report.body.id === 'HeavyAdIntervention'
    );

    if (!filteredReports.length) return;
    this.onInterventionCallback(filteredReports);
    this.destroy();
  }

  private shouldHandleHeavyAdInterventions(): boolean {
    return isChromeBrowser() && !isIos();
  }

  private getIframesToWatchForHeavyIntervention(
    currentWindow: Window
  ): HTMLIFrameElement[] {
    const framesList: HTMLIFrameElement[] = [this.rootFrame];

    try {
      let parentFrame = currentWindow.parent;

      while (parentFrame && parentFrame !== window.top) {
        framesList.push(
          (parentFrame.frameElement as unknown) as HTMLIFrameElement
        );
        parentFrame = parentFrame.parent;
      }
    } catch {
      DebugLog.log('error when getting frameElement of parent window');
    }

    return framesList;
  }
}
